import React, { useEffect, useRef } from "react";

/**
 * Layered, cinematic hero media:
 *   Layer 1: image with Ken Burns drift
 *   Layer 2: diagonal light sheen sweep
 *   Layer 3: animated film grain (inline SVG noise)
 * Wrapper: scroll parallax at 0.22x, only while hero is in view.
 * All motion respects `prefers-reduced-motion: reduce` via CSS.
 */
const NOISE_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320'>
     <filter id='n'>
       <feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='1' stitchTiles='stitch' seed='4'/>
       <feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1 0 0 0 0'/>
     </filter>
     <rect width='100%' height='100%' filter='url(#n)'/>
   </svg>`
);

const HeroMedia = ({ src, alt = "" }) => {
  const wrapRef = useRef(null);
  const mediaRef = useRef(null);
  const rafRef = useRef(0);
  const inViewRef = useRef(true);

  useEffect(() => {
    const wrap = wrapRef.current;
    const media = mediaRef.current;
    if (!wrap || !media) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;

    let lastY = -1;
    const tick = () => {
      rafRef.current = 0;
      if (!inViewRef.current) return;
      const y = window.scrollY || window.pageYOffset || 0;
      if (y === lastY) return;
      lastY = y;
      const rect = wrap.getBoundingClientRect();
      // Only parallax while hero overlaps viewport
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const offset = y * 0.22;
      media.style.transform = `translate3d(0, ${offset}px, 0)`;
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) inViewRef.current = e.isIntersecting;
      },
      { threshold: 0 }
    );
    io.observe(wrap);

    window.addEventListener("scroll", onScroll, { passive: true });
    tick();

    return () => {
      window.removeEventListener("scroll", onScroll);
      io.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="hero-parallax absolute inset-0 overflow-hidden"
      aria-hidden={alt ? undefined : true}
      data-testid="hero-media"
    >
      <div ref={mediaRef} className="absolute inset-0 will-change-transform">
        {/* Layer 1: Ken Burns image */}
        <img
          src={src}
          alt={alt}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="hero-kenburns absolute w-full h-full object-cover"
          style={{ inset: "-2.5%", width: "105%", height: "105%" }}
        />
        {/* Layer 2: Light sheen */}
        <div
          className="hero-sheen pointer-events-none absolute"
          style={{ inset: "-10%" }}
        />
        {/* Layer 3: Animated grain */}
        <div
          className="hero-grain pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,${NOISE_SVG}")`,
            backgroundRepeat: "repeat",
            backgroundSize: "320px 320px",
          }}
        />
      </div>
    </div>
  );
};

export default HeroMedia;
